import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Building2, User, Upload, FileSignature, Shield,
  Lock, Send, CheckCircle2, FileText, Info, Save,
} from 'lucide-react';
import { toast } from 'sonner';

const OSPEDALE_INFO = {
  ospedale: 'Ospedale San Camillo — Roma',
  reparto: 'Medicina Interna — U.O. 3',
  medico: 'Dr. Marco De Angelis',
  matricola: 'MED-SC-0421',
};

export default function OspedaleRichiesta() {
  const navigate = useNavigate();
  const location = useLocation();
  const draftResume = location.state?.draftData;
  const draftStep = location.state?.draftStep;
  const [step, setStep] = useState(draftStep ?? 0);
  const [data, setData] = useState(draftResume ?? { pazNome: '', pazCognome: '', pazCf: '', pazDataNascita: '', diagnosi: '', motivazione: '', urgenza: 'ordinaria', noteCliniche: '' });
  const [files, setFiles] = useState([]);
  const [pin, setPin] = useState('');
  const [signed, setSigned] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const newFiles = Array.from(e.dataTransfer?.files || []).map(f => ({ name: f.name, size: `${(f.size / 1024).toFixed(0)} KB`, type: f.type }));
    if (newFiles.length) setFiles(prev => [...prev, ...newFiles]);
    else setFiles(prev => [...prev, { name: 'Contact_Assessment_Mod431.pdf', size: '245 KB', type: 'application/pdf' }]);
  };

  const handleMockUpload = () => {
    setFiles(prev => [...prev, { name: `Contact_Assessment_${Date.now()}.pdf`, size: '245 KB', type: 'application/pdf' }]);
    toast.success('Documento caricato');
  };

  const handleSign = () => {
    setSigned(true);
    toast.success('Richiesta firmata e inviata', { description: 'La COT-D ricevera la segnalazione entro 24h.' });
  };

  const handleSaveDraft = () => {
    toast.success('Bozza salvata con successo', { description: 'Potrai riprendere la compilazione dalla Control Room.' });
    navigate('/ospedale');
  };

  return (
    <AppLayout role="ospedale">
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-8" data-testid="ospedale-richiesta">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/ospedale')} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />Torna alla Control Room
          </button>
          <Badge className="text-[11px] font-bold px-3 py-1.5 border-0 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">Rilevazione del Bisogno — Contact Assessment</Badge>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2" data-testid="stepper">
          {['Dati Paziente', 'Upload Documentale', 'Firma e Invio'].map((label, i) => (
            <div key={i} className="flex items-center">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${i < step ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : i === step ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center text-[9px]">{i + 1}</span>}
                {label}
              </div>
              {i < 2 && <div className={`w-8 h-0.5 mx-1 ${i < step ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'}`} />}
            </div>
          ))}
        </div>

        {/* Provenienza (sempre visibile, read-only) */}
        <Card className="border-0 shadow-sm rounded-2xl bg-slate-50/50 dark:bg-slate-800/30" data-testid="provenienza-card">
          <CardContent className="p-5">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" />Provenienza Richiesta</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[{ l: 'Ospedale', v: OSPEDALE_INFO.ospedale }, { l: 'Reparto', v: OSPEDALE_INFO.reparto }, { l: 'Medico Dimettente', v: OSPEDALE_INFO.medico }, { l: 'Matricola', v: OSPEDALE_INFO.matricola }].map(item => (
                <div key={item.l} className="px-3 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                  <p className="text-[9px] text-slate-400 uppercase tracking-wider">{item.l}</p>
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-200 mt-0.5">{item.v}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step 0: Dati Paziente */}
        {step === 0 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-0">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2"><User className="w-4 h-4 text-blue-500" />Dati Paziente</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[{ l: 'Cognome', k: 'pazCognome', ph: 'Inserisci cognome' }, { l: 'Nome', k: 'pazNome', ph: 'Inserisci nome' }, { l: 'Codice Fiscale', k: 'pazCf', ph: 'RSSMRA47A15H501X' }, { l: 'Data di Nascita', k: 'pazDataNascita', ph: 'GG/MM/AAAA' }].map(f => (
                    <div key={f.k}>
                      <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">{f.l}</label>
                      <input type="text" value={data[f.k]} onChange={e => setData(prev => ({ ...prev, [f.k]: e.target.value }))} placeholder={f.ph} className="mt-1 w-full px-3 py-2.5 rounded-xl bg-card border border-border/50 text-xs font-medium text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid={`input-${f.k}`} />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Diagnosi alla Dimissione</label>
                  <Textarea value={data.diagnosi} onChange={e => setData(prev => ({ ...prev, diagnosi: e.target.value }))} placeholder="Descrizione diagnosi principale..." className="mt-1 text-xs min-h-[64px]" data-testid="input-diagnosi" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Motivazione Richiesta ADI</label>
                  <Textarea value={data.motivazione} onChange={e => setData(prev => ({ ...prev, motivazione: e.target.value }))} placeholder="Motivazione clinica per attivazione ADI..." className="mt-1 text-xs min-h-[64px]" data-testid="input-motivazione" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Urgenza</label>
                  <div className="flex gap-2 mt-1">
                    {['ordinaria', 'urgente', 'emergenza'].map(u => (
                      <button key={u} onClick={() => setData(prev => ({ ...prev, urgenza: u }))} className={`px-4 py-2 rounded-xl text-xs font-semibold border-2 transition-all capitalize ${data.urgenza === u ? (u === 'emergenza' ? 'border-red-500 bg-red-50 dark:bg-red-950/20 text-red-700' : u === 'urgente' ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20 text-amber-700' : 'border-blue-500 bg-blue-50 dark:bg-blue-950/20 text-blue-700') : 'border-border/30 text-muted-foreground'}`} data-testid={`urgenza-${u}`}>{u}</button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-between">
              <Button variant="outline" className="gap-2 rounded-xl" onClick={handleSaveDraft} data-testid="btn-bozza-0"><Save className="w-4 h-4" />Salva in Bozza</Button>
              <Button className="gap-2 rounded-xl px-6" onClick={() => setStep(1)} data-testid="btn-avanti-1">Avanti <ArrowLeft className="w-4 h-4 rotate-180" /></Button>
            </div>
          </motion.div>
        )}

        {/* Step 1: Upload Documentale */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-1">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2"><FileText className="w-4 h-4 text-violet-500" />Upload Documentale — Contact Assessment</h3>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40">
                  <Info className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <p className="text-[11px] text-amber-700 dark:text-amber-400">Il modulo Contact Assessment (Mod. 431) è obbligatorio per l'attivazione ADI.</p>
                </div>
                {/* Drag & Drop Area */}
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={handleMockUpload}
                  className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-950/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}
                  data-testid="dropzone"
                >
                  <Upload className={`w-8 h-8 mx-auto mb-3 ${dragOver ? 'text-blue-500' : 'text-slate-300'}`} />
                  <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Trascina qui i file oppure clicca per caricare</p>
                  <p className="text-[10px] text-slate-400 mt-1">PDF, DOCX, JPG — Max 10MB per file</p>
                </div>
                {/* Uploaded Files */}
                {files.length > 0 && (
                  <div className="space-y-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40" data-testid={`file-${i}`}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          <div>
                            <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">{f.name}</p>
                            <p className="text-[10px] text-emerald-500">{f.size}</p>
                          </div>
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Note Cliniche Aggiuntive</label>
                  <Textarea value={data.noteCliniche} onChange={e => setData(prev => ({ ...prev, noteCliniche: e.target.value }))} placeholder="Note aggiuntive per la COT..." className="mt-1 text-xs min-h-[48px]" data-testid="input-note" />
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" className="gap-2 rounded-xl" onClick={() => setStep(0)} data-testid="btn-indietro-1"><ArrowLeft className="w-4 h-4" />Indietro</Button>
                <Button variant="outline" className="gap-2 rounded-xl" onClick={handleSaveDraft} data-testid="btn-bozza-1"><Save className="w-4 h-4" />Salva in Bozza</Button>
              </div>
              <Button className="gap-2 rounded-xl px-6" onClick={() => setStep(2)} data-testid="btn-avanti-2">Avanti <ArrowLeft className="w-4 h-4 rotate-180" /></Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Firma Digitale */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-2">
            <Card className={`border-2 shadow-sm rounded-2xl ${signed ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/10' : 'border-violet-200 dark:border-violet-800/40'}`} data-testid="firma-card">
              <CardContent className="p-6 space-y-5">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2"><Shield className="w-4 h-4 text-violet-500" />Firma Digitale e Invio</h3>
                {signed ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-base font-bold text-emerald-700 dark:text-emerald-400">Richiesta Firmata e Inviata</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400/80 mt-1">Segnalazione trasmessa alla COT-D. Riceverai aggiornamenti sulla Control Room.</p>
                    <Button className="mt-5 gap-2 rounded-xl" onClick={() => navigate('/ospedale')} data-testid="btn-torna-cr">Torna alla Control Room</Button>
                  </div>
                ) : (
                  <>
                    <div className="p-4 rounded-xl bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800/40">
                      <p className="text-xs text-violet-700 dark:text-violet-300 leading-relaxed">Confermo che i dati inseriti e la documentazione allegata sono corretti e completi. La richiesta sarà trasmessa alla Centrale Operativa Territoriale (COT-D) per il triage e l'attivazione del percorso ADI.</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-foreground flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-muted-foreground" />PIN Smart Card</label>
                      <input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder="Inserisci PIN" maxLength={8} className="mt-1.5 w-full max-w-xs px-4 py-3 rounded-xl bg-card border border-border text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-violet-400/50" data-testid="input-pin" />
                    </div>
                    <div className="flex justify-between pt-2">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" className="gap-2 rounded-xl" onClick={() => setStep(1)} data-testid="btn-indietro-2"><ArrowLeft className="w-4 h-4" />Indietro</Button>
                        <Button variant="outline" className="gap-2 rounded-xl" onClick={handleSaveDraft} data-testid="btn-bozza-2"><Save className="w-4 h-4" />Salva in Bozza</Button>
                      </div>
                      <Button className="gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-6" disabled={pin.length < 4} onClick={handleSign} data-testid="btn-firma-invia">
                        <FileSignature className="w-4 h-4" /> Firma e Invia Richiesta all'ASL
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </AppLayout>
  );
}
